export const INBOX_TYPES = {
  WEB: 'Channel::WebWidget',
  FB: 'Channel::FacebookPage',
  TWITTER: 'Channel::TwitterProfile',
  TWILIO: 'Channel::TwilioSms',
  WHATSAPP: 'Channel::Whatsapp',
  API: 'Channel::Api',
  EMAIL: 'Channel::Email',
  TELEGRAM: 'Channel::Telegram',
  LINE: 'Channel::Line',
  SMS: 'Channel::Sms',
};

export const INBOX_FEATURES = {
  REPLY_TO: 'replyTo',
  REPLY_TO_OUTGOING: 'replyToOutgoing',
};

export const API_INBOX_TYPE = {
  WHATSAPP: 'WhatsApp',
  WEBCHAT: 'Webchat',
  WECHAT: 'WeChat',
  AIRBNB: 'AIRBNB',
  GBM: 'GBM',
  VOICE: 'Voice',
  MSMAIL: 'MSMail',
};

export const INBOXMW_AI_SUPPORTED_INBOX_TYPES = [
  API_INBOX_TYPE.WEBCHAT,
  API_INBOX_TYPE.WHATSAPP,
];

// WhatsApp inboxes exist as native channels or as API inboxes flagged via
// additional_attributes.apiInboxType.
export const hasWhatsAppInbox = (inboxes = []) =>
  inboxes.some(
    (inbox) =>
      inbox?.additional_attributes?.apiInboxType === API_INBOX_TYPE.WHATSAPP ||
      inbox.channel_type === INBOX_TYPES.WHATSAPP
  );

// This is a single source of truth for inbox features
// This is used to check if a feature is available for a particular inbox or not
export const INBOX_FEATURE_MAP = {
  [INBOX_FEATURES.REPLY_TO]: [
    INBOX_TYPES.FB,
    INBOX_TYPES.WEB,
    INBOX_TYPES.TWITTER,
    INBOX_TYPES.WHATSAPP,
    INBOX_TYPES.TELEGRAM,
    INBOX_TYPES.API,
  ],
  [INBOX_FEATURES.REPLY_TO_OUTGOING]: [
    INBOX_TYPES.WEB,
    INBOX_TYPES.TWITTER,
    INBOX_TYPES.WHATSAPP,
    INBOX_TYPES.TELEGRAM,
    INBOX_TYPES.API,
  ],
};

export default {
  computed: {
    apiInboxType() {
      return this.inbox?.additional_attributes?.apiInboxType ?? '';
    },
    channelType() {
      return this.inbox?.channel_type;
    },
    whatsAppAPIProvider() {
      return this.inbox?.provider || '';
    },
    isAMicrosoftInbox() {
      return this.isAnEmailChannel && this.inbox?.provider === 'microsoft';
    },
    isAnOutlookInbox() {
      return (
        this.inbox?.additional_attributes?.apiInboxType ===
        API_INBOX_TYPE.MSMAIL
      );
    },
    isAGoogleInbox() {
      return this.isAnEmailChannel && this.inbox?.provider === 'google';
    },
    isAPIInbox() {
      return this.channelType === INBOX_TYPES.API;
    },
    isATwitterInbox() {
      return this.channelType === INBOX_TYPES.TWITTER;
    },
    isAFacebookInbox() {
      return this.channelType === INBOX_TYPES.FB;
    },
    isAWebWidgetInbox() {
      return this.channelType === INBOX_TYPES.WEB;
    },
    isAPIWebWidgetInbox() {
      return (
        this.channelType === INBOX_TYPES.API &&
        this.apiInboxType === API_INBOX_TYPE.WEBCHAT
      );
    },
    isATwilioChannel() {
      return this.channelType === INBOX_TYPES.TWILIO;
    },
    isALineChannel() {
      return this.channelType === INBOX_TYPES.LINE;
    },
    isAnEmailChannel() {
      return this.channelType === INBOX_TYPES.EMAIL;
    },
    isATelegramChannel() {
      return this.channelType === INBOX_TYPES.TELEGRAM;
    },
    isATwilioSMSChannel() {
      const { medium: medium = '' } = this.inbox ?? {};
      return this.isATwilioChannel && medium === 'sms';
    },
    isASmsInbox() {
      return this.channelType === INBOX_TYPES.SMS || this.isATwilioSMSChannel;
    },
    isATwilioWhatsAppChannel() {
      const { medium: medium = '' } = this.inbox ?? {};
      return this.isATwilioChannel && medium === 'whatsapp';
    },
    isAWhatsAppCloudChannel() {
      return (
        this.channelType === INBOX_TYPES.WHATSAPP &&
        this.whatsAppAPIProvider === 'whatsapp_cloud'
      );
    },
    isVoiceInbox() {
      return (
        this.channelType === INBOX_TYPES.API &&
        this.apiInboxType === API_INBOX_TYPE.VOICE
      );
    },
    is360DialogWhatsAppChannel() {
      return (
        this.channelType === INBOX_TYPES.WHATSAPP &&
        this.whatsAppAPIProvider === 'default'
      );
    },
    chatAdditionalAttributes() {
      const { additional_attributes: additionalAttributes } = this.chat || {};
      return additionalAttributes || {};
    },
    isTwitterInboxTweet() {
      return this.chatAdditionalAttributes.type === 'tweet';
    },
    twilioBadge() {
      return `${this.isATwilioSMSChannel ? 'sms' : 'whatsapp'}`;
    },
    twitterBadge() {
      return `${this.isTwitterInboxTweet ? 'twitter-tweet' : 'twitter-dm'}`;
    },
    facebookBadge() {
      return this.chatAdditionalAttributes.type || 'facebook';
    },
    inboxBadge() {
      let badgeKey = '';
      if (this.isATwitterInbox) {
        badgeKey = this.twitterBadge;
      } else if (this.isAFacebookInbox) {
        badgeKey = this.facebookBadge;
      } else if (this.isATwilioChannel) {
        badgeKey = this.twilioBadge;
      } else if (this.isAWhatsAppChannel) {
        badgeKey = 'whatsapp';
      }
      return badgeKey || this.channelType;
    },
    isAWhatsAppChannel() {
      return (
        this.channelType === INBOX_TYPES.WHATSAPP ||
        this.isApiWhatsAppChannel ||
        this.isATwilioWhatsAppChannel
      );
    },

    inboxPhoneNumber() {
      const phoneNumber = this.inbox?.phone_number;
      if (phoneNumber) return phoneNumber;

      const attributesNumber = this.inbox?.additional_attributes?.number;
      if (attributesNumber) return `+${attributesNumber}`;
      return '';
    },

    apiInboxIdentifier() {
      return this.inbox?.additional_attributes?.apiInboxIdentifier ?? '';
    },

    isInboxMWConversation() {
      return !!this.apiInboxIdentifier;
    },

    isInboxMWSupportedAIType() {
      return INBOXMW_AI_SUPPORTED_INBOX_TYPES.includes(this.apiInboxType);
    },

    isStatusDrivenAIConversation() {
      return this.isInboxMWConversation && this.isInboxMWSupportedAIType;
    },

    isPhoenixInboxMWConversation() {
      return this.isStatusDrivenAIConversation;
    },

    isAiSupportedPhoenixInbox() {
      return this.isInboxMWSupportedAIType;
    },

    isApiWhatsAppChannel() {
      return this.apiInboxType === API_INBOX_TYPE.WHATSAPP;
    },
    isApiWAWithIdentifier() {
      return this.isApiWhatsAppChannel && !!this.apiInboxIdentifier;
    },

    isGoogleBusinessMessengerChannel() {
      return this.apiInboxType === API_INBOX_TYPE.GBM;
    },

    isWeChatChannel() {
      return this.apiInboxType === API_INBOX_TYPE.WECHAT;
    },

    isAirBnbChannel() {
      return this.apiInboxType === API_INBOX_TYPE.AIRBNB;
    },
  },
  methods: {
    inboxHasFeature(feature) {
      return INBOX_FEATURE_MAP[feature]?.includes(this.channelType) ?? false;
    },
  },
};
